import React, { useState, useEffect, useRef } from "react";
import { useBLE } from "../../../hooks/useBle"; // Adjust path as needed
import { useMediaQuery } from "../../../utils/useMediaQuery";

const ProgressMeter = () => {
  const [progress, setProgress] = useState(0.5); // Normalized progress (0-1)
  const [isSimulatorActive, setIsSimulatorActive] = useState(false);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const simulatorRef = useRef<NodeJS.Timeout | null>(null);
  const isTablet = useMediaQuery("(min-width: 1024px)");

  // Get BLE context data
  const {
    isConnected,
    gaugeValue,
    isPulseOximeterConnected,
    pulseOximeterData,
  } = useBLE();

  const minValue = -70; // cmH2O
  const maxValue = 70; // cmH2O
  const totalRange = maxValue - minValue; // 140

  // Track screen width changes
  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Convert gauge value to progress for center-based filling
  const valueToProgress = (value: number): number => {
    const clampedValue = Math.max(minValue, Math.min(maxValue, value));
    return clampedValue; // Keep actual value for center-based rendering
  };

  // Get responsive dimensions
  const getResponsiveDimensions = () => {
    // Calculate appropriate heights based on screen size
    let svgHeight, viewBoxHeight, progressBarHeight, fontSize, tickHeight;

    if (screenWidth < 768) {
      svgHeight = 80;
      viewBoxHeight = 110;
      progressBarHeight = 18;
      fontSize = { small: 12, large: 20 };
      tickHeight = { small: 15, large: 25 };
    } else if (screenWidth < 1024) {
      svgHeight = 100;
      viewBoxHeight = 130;
      progressBarHeight = 22;
      fontSize = { small: 14, large: 24 };
      tickHeight = { small: 18, large: 28 };
    } else {
      svgHeight = 120;
      viewBoxHeight = 150;
      progressBarHeight = 26;
      fontSize = { small: 16, large: 28 };
      tickHeight = { small: 20, large: 30 };
    }

    // 🎯 ANTI-CROPPING PADDING: Responsive padding based on font sizes
    let edgePadding;
    if (screenWidth < 768) {
      edgePadding = 30; // Mobile: 20px font needs ~30px space for "-70"
    } else if (screenWidth < 1024) {
      edgePadding = 35; // Tablet: 24px font needs ~35px space
    } else {
      edgePadding = 40; // Large: 28px font needs ~40px space
    }

    // console.log(`🎯 PERFECT ALIGNMENT SYSTEM:`);
    // console.log(`Screen: ${screenWidth}px, Edge Padding: ${edgePadding}px`);
    // console.log(
    //   `Label Span: ${screenWidth - edgePadding * 2}px (${(
    //     ((screenWidth - edgePadding * 2) / screenWidth) *
    //     100
    //   ).toFixed(1)}% usage)`
    // );

    return {
      svgHeight,
      viewBoxHeight,
      progressBarHeight,
      fontSize,
      tickHeight,
      edgePadding,
    };
  };

  // Intelligent tick generation
  const generateTickValues = (): { value: number; isMain: boolean }[] => {
    // Always include main ticks (multiples of 10)
    const mainTicks = [];
    for (let i = -70; i <= 70; i += 10) {
      mainTicks.push({ value: i, isMain: true });
    }

    // Add intermediate ticks on larger screens
    let allTicks = [...mainTicks];
    if (screenWidth >= 768) {
      const intermediateTicks = [];
      for (let i = -65; i <= 65; i += 10) {
        intermediateTicks.push({ value: i, isMain: false });
      }
      allTicks = [...allTicks, ...intermediateTicks].sort(
        (a, b) => a.value - b.value
      );
    }

    return allTicks;
  };

  // Simulator functions
  const startSimulator = () => {
    if (simulatorRef.current) {
      clearInterval(simulatorRef.current);
    }

    setIsSimulatorActive(true);
    simulatorRef.current = setInterval(() => {
      const randomValue = minValue + Math.random() * totalRange;
      const smoothedValue = progress + (randomValue - progress) * 0.3;
      setProgress(smoothedValue);
    }, 150);
  };

  const stopSimulator = () => {
    if (simulatorRef.current) {
      clearInterval(simulatorRef.current);
      simulatorRef.current = null;
    }
    setIsSimulatorActive(false);
  };

  // Update progress from BLE data
  useEffect(() => {
    if (isConnected && gaugeValue !== undefined && !isSimulatorActive) {
      setProgress(valueToProgress(gaugeValue));
    } else if (!isConnected && !isSimulatorActive) {
      setProgress(0);
    }
  }, [isConnected, gaugeValue, isSimulatorActive]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (simulatorRef.current) {
        clearInterval(simulatorRef.current);
      }
    };
  }, []);

  const getColors = () => {
    if (isConnected || isSimulatorActive) {
      return {
        textColor: "#FFFFFF",
        tickColor: "#6D7980",
        backgroundColor: "#1C1C1E",
        progressColor: "#00FFFF", // Cyan for pressure
        plxSpO2Color: "#00FF88", // Light green for SpO2
        plxPulseColor: "#FF6B6B", // Light coral for pulse rate
        opacity: 1,
      };
    } else {
      return {
        textColor: "#666666",
        tickColor: "#3A3A3C",
        backgroundColor: "#1A1A1C",
        progressColor: "#004A4A",
        plxSpO2Color: "#004A44", // Dimmed versions when disconnected
        plxPulseColor: "#4A2B2B",
        opacity: 0.5,
      };
    }
  };

  const colors = getColors();
  const dimensions = getResponsiveDimensions();
  const tickValues = generateTickValues();
  const currentValue = progress;

  // Calculate positions with FULL WIDTH approach
  const getCenterPosition = (): number => {
    return (0 - minValue) / totalRange;
  };

  const centerPos = getCenterPosition();
  const currentPos = (currentValue - minValue) / totalRange;

  // Progress bar spans to EXACTLY match label positioning
  const getProgressBarProps = () => {
    if (!isConnected && !isSimulatorActive) {
      return { x: 0, width: 0, visible: false };
    }

    // 🎯 PERFECT ALIGNMENT: Use exact same calculations as labels
    const { edgePadding } = dimensions;
    const labelSpanWidth = screenWidth - edgePadding * 2;
    const barStartX = edgePadding;

    if (currentValue >= 0) {
      return {
        x: barStartX + centerPos * labelSpanWidth,
        width: (currentPos - centerPos) * labelSpanWidth,
        visible: true,
      };
    } else {
      return {
        x: barStartX + currentPos * labelSpanWidth,
        width: (centerPos - currentPos) * labelSpanWidth,
        visible: true,
      };
    }
  };

  // Format PLX data for display
  const formatPLXData = () => {
    if (!isPulseOximeterConnected || !pulseOximeterData) {
      return null;
    }

    const spO2Display =
      pulseOximeterData.spO2 !== null
        ? `${pulseOximeterData.spO2.toFixed(0)}%`
        : "--";

    const pulseDisplay =
      pulseOximeterData.pulseRate !== null
        ? `${pulseOximeterData.pulseRate.toFixed(0)}`
        : "--";

    return {
      spO2: spO2Display,
      pulse: pulseDisplay,
      isValid:
        pulseOximeterData.spO2 !== null || pulseOximeterData.pulseRate !== null,
    };
  };

  const renderScale = () => {
    const progressProps = getProgressBarProps();
    const { edgePadding } = dimensions;
    // 🎯 PERFECT ALIGNMENT: Use exact same width calculation as labels
    const labelSpanWidth = screenWidth - edgePadding * 2;

    // 🎯 DEFINE PRESSURE BAR POSITION: Used throughout the function
    const pressureBarTop =
      dimensions.viewBoxHeight - dimensions.progressBarHeight - 15;
    const plxData = formatPLXData();

    return (
      <div className="w-full">
        {/* 🎯 HEADER: Separated from SVG to prevent overlap */}
        <div className="flex justify-between items-center mb-2 mt-2">
          {/* Left: PLX Data (SpO2) */}
          {plxData ? (
            <span
              style={{
                color: colors.plxSpO2Color,
                transition: "color 0.3s ease",
                minWidth: "70px",
                maxWidth: "70px",
                textAlign: "left",
                display: "inline-block",
                pointerEvents: "none",
                zIndex: 1,
              }}
              className="text-xs font-mono"
            >
              SpO₂: {plxData.spO2}
            </span>
          ) : (
            <span style={{ minWidth: "48px" }} />
          )}

          {/* Center: PLX Data (Pulse/Heart Rate) */}
          {plxData ? (
            <span
              className="flex items-center justify-center text-xs font-mono"
              style={{
                color: colors.plxPulseColor,
                transition: "color 0.3s ease",
                minWidth: "90px",
                maxWidth: "90px",
                textAlign: "center",
                letterSpacing: "1px",
                whiteSpace: "nowrap",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                style={{
                  verticalAlign: "middle",
                  animation:
                    isConnected || isSimulatorActive
                      ? "heartbeat-blink 1s infinite"
                      : undefined,
                  marginRight: "2px",
                }}
                xmlns="http://www.w3.org/2000/svg"
              >
                <style>
                  {`
            @keyframes heartbeat-blink {
              0%, 100% { opacity: 1; }
              10% { opacity: 0.3; }
              20% { opacity: 1; }
              30% { opacity: 0.3; }
              40% { opacity: 1; }
            }
          `}
                </style>
                <path
                  d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41 0.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
                  fill={colors.plxPulseColor}
                />
              </svg>
              : {plxData.pulse} bpm
              <style>
                {`
          @keyframes heartbeat {
            0%, 100% { transform: scale(1); }
            10% { transform: scale(1.2); }
            20% { transform: scale(1); }
            30% { transform: scale(1.2); }
            40% { transform: scale(1); }
          }
          `}
              </style>
            </span>
          ) : (
            <span style={{ minWidth: "80px" }} />
          )}

          {/* Right: Unit Label */}
          <span
            className="text-xs"
            style={{
              color: colors.textColor,
              transition: "color 0.3s ease",
            }}
          >
            cmH₂O
          </span>
        </div>

        {/* 🎯 PERFECTLY ALIGNED SVG */}
        <svg
          className="w-full"
          style={{
            height: `${dimensions.svgHeight + 20}px`, // Extra height for clean layout
            display: "block",
            marginTop: "8px", // Clear separation from header
          }}
          viewBox={`0 0 ${screenWidth} ${dimensions.viewBoxHeight + 20}`}
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* 🎯 PERFECTLY ALIGNED PRESSURE BAR - spans from -70 label to 70 label */}
          <rect
            x={edgePadding}
            y={pressureBarTop}
            width={labelSpanWidth}
            height={dimensions.progressBarHeight}
            fill={colors.backgroundColor}
            rx="4"
            style={{ transition: "fill 0.3s ease" }}
          />

          {/* Tick marks and labels */}
          {tickValues.map((tick, index) => {
            const normalizedPosition = (tick.value - minValue) / totalRange;

            // 🎯 LABEL POSITIONING: Perfect alignment with pressure bar
            const x = edgePadding + normalizedPosition * labelSpanWidth;

            const isMainTick = tick.isMain;
            const shouldBeBold = [0, 70, -70].includes(tick.value);

            let tickStartY, tickEndY, labelY;

            if (shouldBeBold) {
              labelY = isTablet ? 45 : 50; // Positioned below header
              tickStartY = isTablet ? 65 : 55;
              tickEndY = pressureBarTop;
            } else if (isMainTick) {
              labelY = isTablet ? 50 : 60;
              tickStartY = isTablet ? 70 : 65;
              tickEndY = pressureBarTop;
            } else {
              labelY = isTablet ? 55 : 65;
              tickStartY = isTablet ? 75 : 75;
              tickEndY = pressureBarTop;
            }

            return (
              <g key={`${tick.value}-${index}`}>
                {/* Labels for main ticks */}
                {(isMainTick || shouldBeBold) && (
                  <text
                    x={x}
                    y={labelY}
                    fill={colors.textColor}
                    fontSize={
                      shouldBeBold
                        ? dimensions.fontSize.large
                        : dimensions.fontSize.small
                    }
                    fontWeight={shouldBeBold ? "700" : "500"}
                    textAnchor="middle"
                    fontFamily="Figtree,system-ui, -apple-system, sans-serif"
                    style={{ transition: "fill 0.3s ease" }}
                  >
                    {tick.value}
                  </text>
                )}

                {/* Variable height tick lines */}
                <path
                  d={`M${x} ${tickStartY} L${x} ${tickEndY}`}
                  stroke={colors.tickColor}
                  strokeWidth={shouldBeBold ? "3" : isMainTick ? "2" : "1"}
                  strokeLinecap="round"
                  style={{ transition: "stroke 0.3s ease" }}
                  opacity={shouldBeBold ? 1 : isMainTick ? 1 : 0.4}
                />
              </g>
            );
          })}

          {/* 🎯 CENTER LINE with perfect alignment */}
          <path
            d={`M${
              edgePadding + centerPos * labelSpanWidth
            } ${pressureBarTop} L${edgePadding + centerPos * labelSpanWidth} ${
              pressureBarTop + dimensions.progressBarHeight
            }`}
            stroke={colors.tickColor}
            strokeWidth="2"
            strokeOpacity="0.5"
          />

          {/* 🎯 PROGRESS INDICATOR with perfect alignment */}
          {progressProps.visible && (
            <rect
              x={progressProps.x}
              y={pressureBarTop}
              width={Math.abs(progressProps.width)}
              height={dimensions.progressBarHeight}
              fill={colors.progressColor}
              rx="4"
              style={{
                transition: "width 0.2s ease, fill 0.3s ease, x 0.2s ease",
              }}
            />
          )}
        </svg>
      </div>
    );
  };

  return (
    <div>
      {/* Perfectly aligned pressure bar */}
      {renderScale()}

      {/* Debug controls */}
      {/* <div className="flex gap-2 mt-2 justify-center">
        <button
          onClick={startSimulator}
          className="px-3 py-1 bg-blue-500 text-white rounded text-xs"
        >
          Start Simulator
        </button>
        <button
          onClick={stopSimulator}
          className="px-3 py-1 bg-red-500 text-xs"
        >
          Stop Simulator
        </button>
      </div> */}
    </div>
  );
};

export default ProgressMeter;
